import { ProxyHttpClient, ProxyRequestOptions } from './proxy-http-client'

export type AgentKind =
  | 'query_agent'
  | 'ingest_query_agent'
  | 'ingest_agent'
  | 'ingest_section_parser'
  | 'ingest_reconciler'
  | 'integrations_agent'
  | 'custom_agent_builder'
  | 'custom_agent_follow_up'
  | 'dashboard_builder'
export type AgentWriteState = 'awaiting_user' | 'processing' | 'done' | 'error'
/** Graph-only sub-kind; set when `actionKind` is `cypher_write` or `node_evidence`. */
export type AgentWriteChangeKind =
  | 'create_nodes'
  | 'create_relationships'
  | 'update_properties'
  | 'node_evidence'
  | 'delete'
/**
 * Top-level discriminator the apply path switches on. `cypher_write` and
 * `node_evidence` are graph actions (they carry a `changeKind` + `cypher`);
 * `graph_write` is the structured-envelope successor to `cypher_write`
 * (envelope in `params`, no cypher). `servicenow_incident` is the first
 * non-graph action (payload in `params`). `create_writeback_tool` and
 * `bind_writeback_tool` are non-graph custom-agent-builder HITL: Apply
 * persists the org writeback-tool registry row and/or config binding.
 */
export type AgentWriteActionKind =
  | 'cypher_write'
  | 'graph_write'
  | 'node_evidence'
  | 'servicenow_incident'
  | 'create_branch'
  // Human-in-the-loop pause: the agent asks the operator a question and blocks
  // on the row settling (answer in `result`). Non-graph — not branch-gated.
  | 'user_question'
  // Inverse channel: an unsolicited operator message queued for a running agent
  // to drain at its next turn boundary (text in `params.message`). Non-graph —
  // not branch-gated.
  | 'user_message'
  | 'create_writeback_tool'
  | 'bind_writeback_tool'
  | 'tag_write'
  | 'tag_write_policy'
  // Registry history-collection change (register / enable / disable / change
  // source). Org-scoped Postgres, not graph — not branch-gated.
  | 'tag_historization'
  | 'update_custom_agent_config'
  | 'custom_agent_run'
  | 'condition_vocabulary'
  | 'indicates'

/**
 * Action kinds gated by branch protection: their apply mutates the ontology
 * graph, so it must land on a writable (non-protected) branch — applies on
 * main are refused. Non-graph actions apply regardless of the active branch
 * (`create_branch` exists precisely because the caller is on main;
 * `servicenow_incident` never touches the graph). Extend when new
 * graph-mutating action kinds are added.
 */
export const PROTECTED_BRANCH_GATED_ACTION_KINDS = [
  'cypher_write',
  'graph_write',
  'node_evidence',
  'condition_vocabulary',
  'indicates'
] as const satisfies readonly AgentWriteActionKind[]

export type ProtectedBranchGatedActionKind = (typeof PROTECTED_BRANCH_GATED_ACTION_KINDS)[number]

/**
 * True when an action kind's apply must pass the protected-branch guard.
 * Absent/empty kinds predate the `actionKind` column and are cypher writes,
 * so they gate.
 */
export function isProtectedBranchGatedActionKind(kind: string | undefined): boolean {
  if (kind === undefined || kind === '') return true
  return (PROTECTED_BRANCH_GATED_ACTION_KINDS as readonly string[]).includes(kind)
}

/**
 * Reject reason the chat stamps on undecided proposals when the user sends a
 * new message instead of deciding. Settled tool results key off it to tell
 * the agent to stop rather than propose the next change.
 */
export const AGENT_WRITE_SUPERSEDED_REASON = 'superseded by new user message'

export interface AgentWrite {
  id: string
  conversationId: string
  toolUseId: string
  agentKind: AgentKind
  state: AgentWriteState
  /** Present for legacy `cypher_write` rows; omitted for `graph_write` / non-graph actions. */
  cypher?: string
  params: Record<string, unknown>
  summary: string
  changeKind?: AgentWriteChangeKind
  actionKind: AgentWriteActionKind
  /** Action-specific output recorded on apply (e.g. created incident sys_id/number). */
  result?: Record<string, unknown>
  planId?: string
  planIndex?: number
  planSize?: number
  nodesCreated: number
  nodesModified: number
  nodesDeleted: number
  relationshipsCreated: number
  relationshipsDeleted: number
  totalOperations: number
  error?: string
  submittedAt: string
  updatedAt: string
  completedAt?: string
  /**
   * UTC timestamp set when the UI fired the synthetic follow-up agent
   * turn for this row's `planId`. Acts as the idempotency guard against
   * a refreshed browser / second tab firing the resume turn twice.
   * Stamped server-side via `claimResumeDispatch` in a single
   * transaction across every row of the plan.
   */
  resumeDispatchedAt?: string
}

/**
 * Plan-level summary used by the UI on conversation load to detect plans
 * that finished while the browser was offline. The proxy returns one
 * entry per plan whose rows are ALL terminal AND whose `resume_dispatched_at`
 * is NULL — these are the plans that still need a synthetic follow-up
 * agent turn fired.
 */
export interface SettledUnresumedPlan {
  planId: string
  conversationId: string
  planSize: number
  lastSettledAt: string
}

/**
 * Result of `claimResumeDispatch`. `claimed=true` means this caller is
 * the unique winner and SHOULD fire the follow-up agent turn;
 * `claimed=false` means another caller (refresh / second tab) already
 * stamped the plan and the caller MUST NOT re-fire.
 */
export interface ClaimResumeDispatchResult {
  claimed: boolean
  rowCount: number
  dispatchedAt?: string
  alreadyDispatchedAt?: string
}

export interface CreateAgentWriteInput {
  id: string
  conversationId: string
  toolUseId: string
  agentKind: AgentKind
  state?: AgentWriteState
  /** Required for legacy `cypher_write`; omitted for `graph_write` / non-graph actions. */
  cypher?: string
  params?: Record<string, unknown>
  summary: string
  /** Graph-only; omit for non-graph actions. */
  changeKind?: AgentWriteChangeKind
  actionKind: AgentWriteActionKind
  planId?: string
  planIndex?: number
  planSize?: number
}

export interface UpdateAgentWriteInput {
  /** Compare-and-set guard for approval decisions. */
  expectedState?: AgentWriteState
  state?: AgentWriteState
  error?: string
  completedAt?: string
  result?: Record<string, unknown>
  nodesCreated?: number
  nodesModified?: number
  nodesDeleted?: number
  relationshipsCreated?: number
  relationshipsDeleted?: number
  totalOperations?: number
}

export interface ListAgentWritesFilters {
  state?: AgentWriteState
  conversationId?: string
  agentKind?: AgentKind
  actionKind?: AgentWriteActionKind
  /** Match any of these action kinds. Older proxy-servers ignore it, so re-check each row. */
  actionKinds?: AgentWriteActionKind[]
  page_size?: number
  page_token?: string
}

export interface AgentWriteListPage {
  agent_writes: AgentWrite[]
  next_page_token?: string
  total_size: number
}

const BASE_PATH = '/api/v1/ontology/agent-writes'

/** Exported for unit tests — keeps query serialization next to the client. */
export function buildAgentWritesQuery(filters?: ListAgentWritesFilters): string {
  if (!filters) return ''
  const params = new URLSearchParams()
  if (filters.state) params.set('state', filters.state)
  if (filters.conversationId) params.set('conversationId', filters.conversationId)
  if (filters.agentKind) params.set('agentKind', filters.agentKind)
  if (filters.actionKind) params.set('actionKind', filters.actionKind)
  if (filters.actionKinds?.length) params.set('actionKinds', filters.actionKinds.join(','))
  if (filters.page_size !== undefined) params.set('page_size', String(filters.page_size))
  if (filters.page_token) params.set('page_token', filters.page_token)
  const q = params.toString()
  return q ? `?${q}` : ''
}

function buildQuery(filters?: ListAgentWritesFilters): string {
  return buildAgentWritesQuery(filters)
}

export class ProxyAgentWritesClient {
  constructor(private readonly http: ProxyHttpClient) {}

  async createWrite(
    input: CreateAgentWriteInput,
    options?: ProxyRequestOptions
  ): Promise<AgentWrite> {
    return this.http.post<AgentWrite>(BASE_PATH, input, options)
  }

  async getWrite(id: string, options?: ProxyRequestOptions): Promise<AgentWrite> {
    return this.http.get<AgentWrite>(`${BASE_PATH}/${encodeURIComponent(id)}`, options)
  }

  async getWriteByToolUseId(toolUseId: string, options?: ProxyRequestOptions): Promise<AgentWrite> {
    return this.http.get<AgentWrite>(
      `${BASE_PATH}/by-tool-use/${encodeURIComponent(toolUseId)}`,
      options
    )
  }

  async listWrites(
    filters?: ListAgentWritesFilters,
    options?: ProxyRequestOptions
  ): Promise<AgentWrite[]> {
    const page = await this.listWritesPage(filters, options)
    return page?.agent_writes ?? []
  }

  async listWritesPage(
    filters?: ListAgentWritesFilters,
    options?: ProxyRequestOptions
  ): Promise<AgentWriteListPage> {
    return this.http.get<AgentWriteListPage>(`${BASE_PATH}${buildQuery(filters)}`, options)
  }

  async patchWrite(
    id: string,
    patch: UpdateAgentWriteInput,
    options?: ProxyRequestOptions
  ): Promise<AgentWrite> {
    return this.http.request<AgentWrite>('PATCH', `${BASE_PATH}/${encodeURIComponent(id)}`, {
      ...options,
      body: patch
    })
  }

  async deleteWrite(id: string, options?: ProxyRequestOptions): Promise<void> {
    await this.http.delete(`${BASE_PATH}/${encodeURIComponent(id)}`, options)
  }

  /**
   * Atomically stamp `resume_dispatched_at = now()` on every row of a
   * plan whose stamp is currently NULL. Returns `claimed=true` only on
   * the unique winner — losers see `claimed=false` and must skip firing
   * the follow-up agent turn.
   *
   * 404 surfaces as a thrown error when the plan does not exist.
   */
  async claimResumeDispatch(
    planId: string,
    options?: ProxyRequestOptions
  ): Promise<ClaimResumeDispatchResult> {
    return this.http.post<ClaimResumeDispatchResult>(
      `${BASE_PATH}/by-plan/${encodeURIComponent(planId)}/claim-resume-dispatch`,
      undefined,
      options
    )
  }

  /**
   * List plans for a conversation whose rows are all terminal and that
   * have not yet had a synthetic follow-up agent turn fired. Returns
   * one entry per plan; the UI iterates them on conversation load,
   * claims each, and fires the resume turn for the winners.
   */
  async listSettledUnresumedPlans(
    conversationId: string,
    options?: ProxyRequestOptions
  ): Promise<SettledUnresumedPlan[]> {
    const params = new URLSearchParams({ conversationId })
    const result = await this.http.get<{ plans: SettledUnresumedPlan[] }>(
      `${BASE_PATH}/settled-unresumed?${params.toString()}`,
      options
    )
    return result?.plans ?? []
  }
}
